const ROOT_SELECTOR = '[data-onyx-advent]';
const TOGGLE_SELECTOR = '[data-advent-snow-toggle]';
const STORAGE_PAUSED = 'sigma:onyx-advent:snow-paused';
const STORAGE_SEED = 'sigma:onyx-advent:snow-seed';

/*
 * Motion storyboard, adapted for this vanilla canvas effect:
 * 1. A restrained field is already in progress when the page is ready.
 * 2. Near flakes drift faster and wider than distant flakes while sharing one wind.
 * 3. A few flakes settle on real upper edges, leaving shallow irregular dusting.
 * 4. Pause is immediate and preserves the scene; resume continues without a jump.
 * 5. Hidden tabs, removed sections, and reduced-motion stop the frame loop immediately.
 */
const SETTINGS = Object.freeze({
  desktopParticles: 96,
  mobileParticles: 42,
  mobileBreakpoint: 640,
  dprCap: 1.75,
  maxDelta: 1 / 30,
  gravity: 15,
  minTerminal: 23,
  maxTerminal: 58,
  surfaceBinWidth: 7,
  maxPileHeight: 6.5,
  doorLimit: 6,
});

let activeSnow = null;

function safeStorageGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (_error) {
    return null;
  }
}

function safeStorageSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (_error) {
    // Storage can be blocked in privacy modes. The effect still works for this page view.
  }
}

function sessionSeed() {
  const stored = Number.parseInt(safeStorageGet(STORAGE_SEED) || '', 10);
  if (Number.isInteger(stored) && stored > 0) return stored >>> 0;

  let seed = 0;
  if (window.crypto?.getRandomValues) {
    seed = window.crypto.getRandomValues(new Uint32Array(1))[0];
  } else {
    seed = Math.floor(Math.random() * 0xffffffff);
  }
  seed = seed || 1;
  safeStorageSet(STORAGE_SEED, String(seed));
  return seed;
}

function mulberry32(seed) {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let result = value;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

function supportsEventListenerSignal() {
  return typeof AbortController !== 'undefined';
}

class AdventSnow {
  constructor(root) {
    this.root = root;
    this.toggle = root.querySelector(TOGGLE_SELECTOR);
    this.seed = sessionSeed();
    this.random = mulberry32(this.seed);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.userPaused = safeStorageGet(STORAGE_PAUSED) === 'true';
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'onyx-advent-snow';
    this.canvas.setAttribute('aria-hidden', 'true');
    this.particles = [];
    this.surfaceProfiles = new Map();
    this.surfaces = [];
    this.sprites = [];
    this.colours = { snow: '', shadow: '' };
    this.width = 0;
    this.height = 0;
    this.dpr = 1;
    this.elapsed = 0;
    this.lastTime = 0;
    this.frameId = 0;
    this.refreshId = 0;
    this.destroyed = false;
    this.abortController = supportsEventListenerSignal() ? new AbortController() : null;

    try {
      // Keep this transparent overlay in the normal page compositor. Low-latency
      // desynchronized canvases can obscure the page on mobile graphics drivers.
      this.context = this.canvas.getContext('2d', { alpha: true, desynchronized: false });
      if (!this.context || this.context.getContextAttributes?.().alpha === false) {
        this.destroy();
        return;
      }
      document.body.append(this.canvas);
      this.bind();
      this.resize();
      this.refreshSurfaces();
      this.seedParticles();
      this.updateToggle();
      this.render();
      this.syncAnimation();
    } catch (_error) {
      // Decoration must never leave an unusable overlay when canvas setup fails.
      this.destroy();
    }
  }

  bind() {
    const options = this.abortController ? { signal: this.abortController.signal } : undefined;
    this.onResize = () => this.scheduleRefresh(true);
    this.onVisibility = () => this.syncAnimation();
    this.onContextLost = () => this.destroy();
    this.onReducedMotion = () => {
      this.updateToggle();
      this.syncAnimation();
      this.render();
    };
    this.onToggle = () => {
      this.userPaused = !this.userPaused;
      safeStorageSet(STORAGE_PAUSED, String(this.userPaused));
      this.updateToggle();
      this.syncAnimation();
      this.render();
    };

    window.addEventListener('resize', this.onResize, options);
    document.addEventListener('visibilitychange', this.onVisibility, options);
    this.reducedMotion.addEventListener?.('change', this.onReducedMotion, options);
    this.toggle?.addEventListener('click', this.onToggle, options);
    this.canvas.addEventListener('contextlost', this.onContextLost, options);

    this.mutationObserver = new MutationObserver((records) => {
      const onlyPileCanvasChanges = records.every((record) => (
        record.target instanceof Element && record.target.matches('.onyx-advent-snow-pile')
      ));
      if (!onlyPileCanvasChanges) this.scheduleRefresh(false);
    });
    this.mutationObserver.observe(this.root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'style'],
    });
    this.removalObserver = new MutationObserver(() => {
      if (!this.root.isConnected) this.destroy();
    });
    this.removalObserver.observe(document.documentElement, { childList: true, subtree: true });
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleRefresh(false));
      this.resizeObserver.observe(this.root);
    }
  }

  isPaused() {
    return this.userPaused || this.reducedMotion.matches || document.hidden || !this.root.isConnected;
  }

  updateToggle() {
    if (!this.toggle) return;
    const paused = this.userPaused || this.reducedMotion.matches;
    // aria-pressed=true means the pause control is engaged.
    this.toggle.hidden = false;
    this.toggle.setAttribute('aria-pressed', String(paused));
    this.toggle.textContent = paused ? 'Resume snow' : 'Pause snow';
    this.updateState();
  }

  motionState() {
    if (this.reducedMotion.matches) return 'reduced-motion';
    if (document.hidden) return 'hidden';
    if (this.userPaused) return 'paused';
    return 'running';
  }

  updateState() {
    const state = this.motionState();
    this.root.dataset.adventSnowState = state;
    this.canvas.dataset.adventSnowState = state;
  }

  resize() {
    const previousWidth = this.width || window.innerWidth;
    const previousHeight = this.height || window.innerHeight;
    this.width = Math.max(1, window.innerWidth);
    this.height = Math.max(1, window.innerHeight);
    this.dpr = Math.min(window.devicePixelRatio || 1, SETTINGS.dprCap);
    this.canvas.width = Math.round(this.width * this.dpr);
    this.canvas.height = Math.round(this.height * this.dpr);
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;
    this.context.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.refreshColours();
    this.createSprites();

    const scaleX = this.width / previousWidth;
    const scaleY = this.height / previousHeight;
    this.particles.forEach((flake) => {
      flake.x *= scaleX;
      flake.y *= scaleY;
    });
    this.reconcileParticleCount();
  }

  particleTarget() {
    return this.width < SETTINGS.mobileBreakpoint
      ? SETTINGS.mobileParticles
      : SETTINGS.desktopParticles;
  }

  seedParticles() {
    const target = this.particleTarget();
    while (this.particles.length < target) {
      this.particles.push(this.makeParticle(true));
    }
  }

  reconcileParticleCount() {
    const target = this.particleTarget();
    if (this.particles.length > target) this.particles.length = target;
    while (this.particles.length < target) this.particles.push(this.makeParticle(true));
  }

  makeParticle(distribute = false) {
    const depth = 0.18 + this.random() * 0.82;
    const radius = 0.65 + depth * 2.05 + this.random() * 0.45;
    const terminal = SETTINGS.minTerminal + depth * (SETTINGS.maxTerminal - SETTINGS.minTerminal);
    return {
      x: this.random() * this.width,
      y: distribute ? this.random() * this.height : -radius * 3,
      previousY: -radius * 3,
      vx: (this.random() - 0.5) * 5,
      vy: terminal * (0.45 + this.random() * 0.38),
      radius,
      depth,
      terminal,
      phase: this.random() * Math.PI * 2,
      flutter: 0.55 + this.random() * 1.15,
      sprite: Math.min(2, Math.floor(depth * 3)),
      opacity: 0.38 + depth * 0.56,
    };
  }

  resetParticle(flake) {
    const replacement = this.makeParticle(false);
    Object.assign(flake, replacement);
    flake.x = this.random() * (this.width + 80) - 40;
  }

  createSprites() {
    const snow = this.colours.snow;
    this.sprites = [0.9, 1.55, 2.45].map((radius) => {
      const size = 18;
      const sprite = document.createElement('canvas');
      sprite.width = size * this.dpr;
      sprite.height = size * this.dpr;
      const context = sprite.getContext('2d');
      context.scale(this.dpr, this.dpr);
      const gradient = context.createRadialGradient(9, 9, 0, 9, 9, radius * 2.2);
      gradient.addColorStop(0, 'oklch(1 0 0)');
      gradient.addColorStop(0.46, 'oklch(1 0 0 / 0.88)');
      gradient.addColorStop(1, 'oklch(1 0 0 / 0)');
      context.fillStyle = gradient;
      context.fillRect(0, 0, size, size);
      context.globalCompositeOperation = 'source-in';
      context.fillStyle = snow;
      context.fillRect(0, 0, size, size);
      return sprite;
    });
  }

  scheduleRefresh(resize) {
    this.needsResize = this.needsResize || resize;
    if (this.refreshId) return;
    this.refreshId = requestAnimationFrame(() => {
      this.refreshId = 0;
      if (this.needsResize) this.resize();
      this.needsResize = false;
      this.refreshSurfaces();
      if (this.isPaused()) this.render();
    });
  }

  candidateElements() {
    const featureChip = this.root.querySelector('.onyx-advent__highlights .coffee-product__attribute');
    const countdownBox = this.root.querySelector(
      '.restock-rocket-preorder-countdown-timer .countdown-unit [data-stoq-countdown-overlay]',
    ) || this.root.querySelector(
      '.restock-rocket-preorder-countdown-timer .countdown-box[data-unit="days"]',
    );
    const primary = [
      ...this.root.querySelectorAll('.coffee-product__add-button:not(.coffee-product__sticky-purchase-add), .restock-rocket-preorder-button'),
      featureChip,
      countdownBox,
    ];
    const doors = [...this.root.querySelectorAll('.onyx-advent__door')];
    const doorCount = Math.min(doors.length, SETTINGS.doorLimit);
    const selectedDoors = Array.from({ length: doorCount }, (_value, index) => (
      doors[Math.floor(((index + 0.5) * doors.length) / doorCount)]
    ));
    return [...new Set([...primary.filter(Boolean), ...selectedDoors])];
  }

  surfaceAnchor(element) {
    const ownPosition = window.getComputedStyle(element).position;
    if (ownPosition === 'fixed' || ownPosition === 'sticky') return null;
    let current = element.parentElement;
    while (current && current !== this.root.parentElement) {
      const position = window.getComputedStyle(current).position;
      if (position === 'fixed') return null;
      if (position === 'sticky') return current;
      current = current.parentElement;
    }
    return document.body;
  }

  surfaceKind(element) {
    if (element.matches('.coffee-product__add-button, .restock-rocket-preorder-button')) return 'cta';
    if (element.matches('.onyx-advent__highlights .coffee-product__attribute')) return 'chip';
    if (element.matches('.countdown-box, .countdown-unit, [data-stoq-countdown-overlay]')) return 'counter';
    if (element.matches('.onyx-advent__door')) return 'door';
    return 'standard';
  }

  pileStyle(kind) {
    if (kind === 'cta') return { maxHeight: 6.5, patches: 4, peakMin: 2.4, peakMax: 5.2, iceCount: 3 };
    if (kind === 'counter') return { maxHeight: 5, patches: 2, peakMin: 1.5, peakMax: 3.5, iceCount: 2 };
    if (kind === 'chip') return { maxHeight: 3, patches: 1, peakMin: 0.7, peakMax: 1.8, iceCount: 0 };
    if (kind === 'door') return { maxHeight: 4.5, patches: 2, peakMin: 0.8, peakMax: 2.6, iceCount: 1 };
    return { maxHeight: 5.5, patches: 2, peakMin: 1, peakMax: 3, iceCount: 1 };
  }

  refreshSurfaces() {
    const coloursChanged = this.refreshColours();
    if (coloursChanged) this.createSprites();
    const retained = new Set();
    this.surfaces = this.candidateElements().flatMap((element) => {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      const anchor = this.surfaceAnchor(element);
      if (
        !anchor ||
        rect.width < 28 ||
        rect.height < 12 ||
        style.display === 'none' ||
        style.visibility === 'hidden'
      ) return [];

      retained.add(element);
      const binCount = Math.max(4, Math.ceil(rect.width / SETTINGS.surfaceBinWidth));
      const kind = this.surfaceKind(element);
      const pileStyle = this.pileStyle(kind);
      let profile = this.surfaceProfiles.get(element);
      if (!profile) {
        const canvas = document.createElement('canvas');
        canvas.className = 'onyx-advent-snow-pile';
        canvas.setAttribute('aria-hidden', 'true');
        const heights = this.seedPile(binCount, pileStyle);
        profile = {
          heights,
          binCount,
          kind,
          maxHeight: pileStyle.maxHeight,
          ice: this.seedIce(heights, pileStyle.iceCount),
          canvas,
          context: canvas.getContext('2d', { alpha: true }),
        };
        this.surfaceProfiles.set(element, profile);
      } else if (profile.binCount !== binCount) {
        profile.heights = this.resamplePile(profile.heights, binCount);
        profile.binCount = binCount;
        profile.ice = this.seedIce(profile.heights, pileStyle.iceCount);
      }
      const documentRect = {
        left: rect.left + window.scrollX,
        right: rect.right + window.scrollX,
        top: rect.top + window.scrollY,
        bottom: rect.bottom + window.scrollY,
        width: rect.width,
        height: rect.height,
      };
      if (profile.canvas.parentElement !== anchor) anchor.append(profile.canvas);
      profile.anchor = anchor;
      const surface = {
        element,
        rect: documentRect,
        viewportRect: rect,
        profile,
        collidable: anchor === document.body,
      };
      this.positionPile(surface);
      this.drawPile(surface);
      return [surface];
    });

    this.surfaceProfiles.forEach((profile, element) => {
      if (!retained.has(element)) {
        profile.canvas.remove();
        this.surfaceProfiles.delete(element);
      }
    });
  }

  positionPile(surface) {
    const { anchor, canvas } = surface.profile;
    const pileHeight = SETTINGS.maxPileHeight + 5;
    canvas.width = Math.max(1, Math.ceil(surface.rect.width * this.dpr));
    canvas.height = Math.max(1, Math.ceil(pileHeight * this.dpr));
    if (anchor === document.body) {
      canvas.style.left = `${surface.rect.left}px`;
      canvas.style.top = `${surface.rect.top - SETTINGS.maxPileHeight - 1}px`;
    } else {
      const anchorRect = anchor.getBoundingClientRect();
      canvas.style.left = `${surface.viewportRect.left - anchorRect.left + anchor.scrollLeft}px`;
      canvas.style.top = `${surface.viewportRect.top - anchorRect.top + anchor.scrollTop - SETTINGS.maxPileHeight - 1}px`;
    }
    canvas.style.width = `${surface.rect.width}px`;
    canvas.style.height = `${pileHeight}px`;
    surface.profile.context.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  seedPile(binCount, pileStyle) {
    const heights = new Float32Array(binCount);
    for (let patch = 0; patch < pileStyle.patches; patch += 1) {
      const centre = Math.floor(this.random() * binCount);
      const radius = 1 + Math.floor(this.random() * Math.max(2, binCount * 0.14));
      const peak = pileStyle.peakMin + this.random() * (pileStyle.peakMax - pileStyle.peakMin);
      for (let offset = -radius; offset <= radius; offset += 1) {
        const index = centre + offset;
        if (index < 0 || index >= binCount) continue;
        const falloff = (1 + Math.cos((offset / (radius + 1)) * Math.PI)) / 2;
        heights[index] = Math.max(heights[index], peak * falloff);
      }
    }
    return heights;
  }

  seedIce(heights, count) {
    const ranked = [...heights.keys()].sort((left, right) => heights[right] - heights[left] || left - right);
    const selected = [];
    for (const bin of ranked) {
      if (selected.length >= count || heights[bin] <= 0.35) break;
      if (selected.some((item) => Math.abs(item.bin - bin) < 3)) continue;
      selected.push({
        bin,
        length: 1.2 + ((this.seed + selected.length * 7) % 13) / 10,
      });
    }
    return selected;
  }

  resamplePile(source, binCount) {
    const output = new Float32Array(binCount);
    if (!source.length) return output;
    for (let index = 0; index < binCount; index += 1) {
      const sourceIndex = Math.round((index / Math.max(1, binCount - 1)) * (source.length - 1));
      output[index] = source[sourceIndex];
    }
    return output;
  }

  deposit(surface, x, amount) {
    const local = (x - surface.rect.left) / surface.rect.width;
    const centre = Math.max(0, Math.min(surface.profile.binCount - 1, Math.floor(local * surface.profile.binCount)));
    const heights = surface.profile.heights;
    const maxHeight = surface.profile.maxHeight;
    heights[centre] = Math.min(maxHeight, heights[centre] + amount);
    if (centre > 0) heights[centre - 1] = Math.min(maxHeight, heights[centre - 1] + amount * 0.42);
    if (centre + 1 < heights.length) heights[centre + 1] = Math.min(maxHeight, heights[centre + 1] + amount * 0.42);
    this.drawPile(surface);
  }

  collide(flake) {
    const documentX = flake.x + window.scrollX;
    const documentY = flake.y + window.scrollY;
    const previousDocumentY = flake.previousY + window.scrollY;
    for (const surface of this.surfaces) {
      if (!surface.collidable) continue;
      const { rect, profile } = surface;
      if (rect.bottom < window.scrollY - 20 || rect.top > window.scrollY + this.height + 20) continue;
      if (documentX < rect.left || documentX > rect.right) continue;
      const local = (documentX - rect.left) / rect.width;
      const bin = Math.max(0, Math.min(profile.binCount - 1, Math.floor(local * profile.binCount)));
      const top = rect.top - profile.heights[bin];
      if (previousDocumentY + flake.radius <= top && documentY + flake.radius >= top) {
        this.deposit(surface, documentX, 0.22 + flake.depth * 0.34);
        return true;
      }
    }
    return false;
  }

  update(delta) {
    this.elapsed += delta;
    const coherentWind = Math.sin(this.elapsed * 0.21) * 8 + Math.sin(this.elapsed * 0.057 + 1.7) * 5;
    for (const flake of this.particles) {
      flake.previousY = flake.y;
      const turbulence = Math.sin(this.elapsed * flake.flutter + flake.phase) * (3 + flake.depth * 8);
      const targetVx = (coherentWind + turbulence) * (0.42 + flake.depth * 0.7);
      const horizontalDrag = 1 - Math.exp(-delta * (0.85 + flake.depth));
      flake.vx += (targetVx - flake.vx) * horizontalDrag;
      flake.vy = Math.min(flake.terminal, flake.vy + SETTINGS.gravity * delta);
      flake.x += flake.vx * delta;
      flake.y += flake.vy * delta;

      if (this.collide(flake) || flake.y > this.height + 18 || flake.x < -45 || flake.x > this.width + 45) {
        this.resetParticle(flake);
      }
    }
  }

  resolvedColour(property, fallback) {
    const value = window.getComputedStyle(this.root).getPropertyValue(property).trim();
    return value || fallback;
  }

  refreshColours() {
    const next = {
      snow: this.resolvedColour('--sc-advent-snow', 'oklch(0.99 0.004 90)'),
      shadow: this.resolvedColour('--sc-advent-snow-shadow', 'oklch(0.36 0.025 160 / 0.2)'),
      shade: this.resolvedColour('--sc-advent-snow-shade', 'oklch(0.85 0.018 230)'),
      ice: this.resolvedColour('--sc-advent-ice', 'oklch(0.9 0.025 225 / 0.55)'),
    };
    const changed = next.snow !== this.colours.snow || next.shadow !== this.colours.shadow;
    this.colours = next;
    return changed;
  }

  drawPile(surface) {
    const { context, heights, ice } = surface.profile;
    if (!context) return;
    const { snow, shadow, shade, ice: iceColour } = this.colours;
    const baseline = SETTINGS.maxPileHeight + 1;
    const binWidth = surface.rect.width / heights.length;
    context.clearRect(0, 0, surface.rect.width, SETTINGS.maxPileHeight + 5);
    const frost = context.createLinearGradient(0, baseline - surface.profile.maxHeight, 0, baseline + 1);
    frost.addColorStop(0, snow);
    frost.addColorStop(0.6, snow);
    frost.addColorStop(1, shade);
    const drawPath = (offset, colour) => {
      let index = 0;
      while (index < heights.length) {
        while (index < heights.length && heights[index] <= 0.08) index += 1;
        if (index >= heights.length) break;
        const start = index;
        while (index < heights.length && heights[index] > 0.08) index += 1;
        const end = index - 1;
        context.beginPath();
        context.moveTo(start * binWidth, baseline + offset);
        // Midpoint curves give each drift a soft, continuous powder silhouette.
        for (let point = start; point <= end; point += 1) {
          const x = (point + 0.5) * binWidth;
          const y = baseline - heights[point] + offset;
          const nextX = point === end ? (end + 1) * binWidth : (point + 1.5) * binWidth;
          const nextY = point === end ? baseline + offset : baseline - heights[point + 1] + offset;
          context.quadraticCurveTo(x, y, (x + nextX) / 2, (y + nextY) / 2);
        }
        context.lineTo((end + 1) * binWidth, baseline + offset);
        context.closePath();
        context.fillStyle = colour;
        context.fill();
      }
    };
    context.save();
    context.shadowColor = shadow;
    context.shadowBlur = 1.5;
    context.shadowOffsetY = 0.7;
    drawPath(0, frost);
    context.restore();
    // Sparse fixed highlights add powder texture without flickering each frame.
    context.fillStyle = snow;
    context.globalAlpha = 0.65;
    for (let bin = 0; bin < heights.length; bin += 2) {
      if (heights[bin] < 1.4) continue;
      const grain = ((bin * 37 + this.seed) % 17) / 17;
      context.beginPath();
      context.ellipse((bin + 0.35 + grain * 0.3) * binWidth,
        baseline - heights[bin] * 0.45, 0.35 + grain * 0.3, 0.22, 0, 0, Math.PI * 2);
      context.fill();
    }
    context.globalAlpha = 1;
    for (const icicle of ice) {
      if (heights[icicle.bin] <= 0.35) continue;
      const x = (icicle.bin + 0.5) * binWidth;
      const glaze = context.createLinearGradient(x - 1.4, 0, x + 1.4, 0);
      glaze.addColorStop(0, iceColour);
      glaze.addColorStop(0.45, snow);
      glaze.addColorStop(1, iceColour);
      context.beginPath();
      context.moveTo(x - 1.4, baseline - 0.5);
      context.bezierCurveTo(x - 0.5, baseline + 0.4, x - 0.45, baseline + icicle.length, x, baseline + icicle.length);
      context.bezierCurveTo(x + 0.4, baseline + icicle.length * 0.7, x + 0.7, baseline + 0.3, x + 1.2, baseline - 0.5);
      context.closePath();
      context.globalAlpha = 0.6;
      context.fillStyle = glaze;
      context.fill();
    }
    context.globalAlpha = 1;
  }

  renderFlakes() {
    const context = this.context;
    const { snow } = this.colours;
    context.fillStyle = snow;
    for (const flake of this.particles) {
      const sprite = this.sprites[flake.sprite];
      const size = 9 + flake.radius * 3.2;
      context.globalAlpha = flake.opacity;
      context.drawImage(sprite, flake.x - size / 2, flake.y - size / 2, size, size);
      if (flake.depth > 0.72) {
        context.beginPath();
        context.arc(flake.x, flake.y, flake.radius * 0.52, 0, Math.PI * 2);
        context.fill();
      }
    }
    context.globalAlpha = 1;
  }

  render() {
    if (this.destroyed || !this.context) return;
    this.context.clearRect(0, 0, this.width, this.height);
    if (!this.reducedMotion.matches) this.renderFlakes();
  }

  tick(time) {
    this.frameId = 0;
    if (this.destroyed || !this.root.isConnected) {
      this.destroy();
      return;
    }
    if (this.isPaused()) return;
    const delta = this.lastTime ? Math.min(SETTINGS.maxDelta, (time - this.lastTime) / 1000) : 1 / 60;
    this.lastTime = time;
    this.update(Math.max(0, delta));
    this.render();
    this.frameId = requestAnimationFrame((nextTime) => this.tick(nextTime));
  }

  syncAnimation() {
    this.updateState();
    if (this.isPaused()) {
      if (this.frameId) cancelAnimationFrame(this.frameId);
      this.frameId = 0;
      this.lastTime = 0;
      return;
    }
    if (!this.frameId) {
      this.lastTime = 0;
      this.frameId = requestAnimationFrame((time) => this.tick(time));
    }
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.frameId) cancelAnimationFrame(this.frameId);
    if (this.refreshId) cancelAnimationFrame(this.refreshId);
    this.abortController?.abort();
    if (!this.abortController) {
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.reducedMotion.removeEventListener?.('change', this.onReducedMotion);
      this.toggle?.removeEventListener('click', this.onToggle);
      this.canvas.removeEventListener('contextlost', this.onContextLost);
    }
    this.mutationObserver?.disconnect();
    this.removalObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.surfaceProfiles.forEach((profile) => profile.canvas.remove());
    this.surfaceProfiles.clear();
    this.canvas.remove();
    if (this.toggle) this.toggle.hidden = true;
    if (activeSnow === this) activeSnow = null;
  }
}

function initialise(scope = document) {
  const root = scope.matches?.(ROOT_SELECTOR) ? scope : scope.querySelector?.(ROOT_SELECTOR);
  if (!root || activeSnow?.root === root) return;
  activeSnow?.destroy();
  activeSnow = new AdventSnow(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
} else {
  initialise();
}

document.addEventListener('shopify:section:load', (event) => initialise(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  if (activeSnow && event.target.contains(activeSnow.root)) activeSnow.destroy();
});
